import React, { useState, useEffect, useCallback } from 'react';
import { COLOR } from '../../components/ss/tokens';
import { API_ORIGIN } from '../../services/apiBase';

const API_BASE = API_ORIGIN;

/* ── Lightbox ─────────────────────────────────────────────────────────────── */
function Lightbox({ item, onClose, onPrev, onNext }) {
  useEffect(() => {
    const handle = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') onPrev();
      if (e.key === 'ArrowRight') onNext();
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [onClose, onPrev, onNext]);

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.92)',
      zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <button onClick={onPrev} style={arrowBtn('left')}>‹</button>

      <div onClick={e => e.stopPropagation()} style={{
        maxWidth: '90vw', maxHeight: '90vh', display: 'flex',
        flexDirection: 'column', alignItems: 'center', gap: 12,
      }}>
        {item.media_type === 'VIDEO' ? (
          <video
            src={`${API_BASE}${item.url}`}
            controls autoPlay
            style={{ maxWidth: '85vw', maxHeight: '80vh', borderRadius: 8 }}
          />
        ) : (
          <img
            src={`${API_BASE}${item.url}`}
            alt={item.caption || ''}
            style={{ maxWidth: '85vw', maxHeight: '80vh', borderRadius: 8, objectFit: 'contain' }}
          />
        )}
        {item.caption && (
          <p style={{ color: COLOR.border, fontSize: 14, textAlign: 'center', maxWidth: 600 }}>
            {item.caption}
          </p>
        )}
      </div>

      <button onClick={onNext} style={arrowBtn('right')}>›</button>

      <button onClick={onClose} style={{
        position: 'absolute', top: 20, right: 24,
        background: 'none', border: 'none', color: '#fff',
        fontSize: 32, cursor: 'pointer', lineHeight: 1,
      }}>×</button>
    </div>
  );
}

const arrowBtn = (side) => ({
  position: 'absolute', [side]: 16,
  background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
  color: '#fff', fontSize: 36, cursor: 'pointer',
  borderRadius: 6, padding: '4px 14px', backdropFilter: 'blur(4px)',
});

/* ── Gallery card ─────────────────────────────────────────────────────────── */
function MediaCard({ item, onClick }) {
  const [hovered, setHovered] = useState(false);
  const imgUrl = `${API_BASE}${item.url}`;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative', borderRadius: 12, overflow: 'hidden',
        cursor: 'pointer', background: COLOR.navy,
        aspectRatio: '4/3',
        transform: hovered ? 'scale(1.02)' : 'scale(1)',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        boxShadow: hovered ? '0 12px 40px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.15)',
      }}
    >
      {item.media_type === 'VIDEO' ? (
        <>
          <video
            src={imgUrl}
            muted
            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
          {/* Play button overlay */}
          <div style={{
            position: 'absolute', inset: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.3)',
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%',
              background: 'rgba(255,255,255,0.9)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 22,
            }}>▶</div>
          </div>
        </>
      ) : (
        <img
          src={imgUrl}
          alt={item.caption || ''}
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      )}

      {/* Hover overlay with caption */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0,
        background: 'linear-gradient(transparent, rgba(0,0,0,0.75))',
        padding: '24px 14px 14px',
        opacity: hovered ? 1 : 0,
        transition: 'opacity 0.2s ease',
      }}>
        {item.caption && (
          <p style={{ color: '#fff', fontSize: 13, margin: 0, lineHeight: 1.4 }}>
            {item.caption}
          </p>
        )}
        <span style={{
          display: 'inline-block', marginTop: 4,
          fontSize: 11, color: 'rgba(255,255,255,0.7)',
          textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>
          {item.category}
        </span>
      </div>

      {item.media_type === 'VIDEO' && (
        <span style={{
          position: 'absolute', top: 10, right: 10,
          background: 'rgba(0,0,0,0.6)', color: '#fff',
          fontSize: 10, padding: '2px 8px', borderRadius: 4,
          textTransform: 'uppercase', letterSpacing: '0.06em',
        }}>video</span>
      )}
    </div>
  );
}

/* ── Main Gallery section ──────────────────────────────────────────────────── */
export default function Gallery({ config }) {
  const [items, setItems] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [categories, setCategories] = useState(['All']);
  const [activeCategory, setActiveCategory] = useState('All');
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/api/v1/media/?active_only=true`)
      .then(r => r.json())
      .then(res => {
        const data = res.data || [];
        setItems(data);
        setFiltered(data);
        const cats = ['All', ...new Set(data.map(m => m.category).filter(Boolean))];
        setCategories(cats);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setFiltered(activeCategory === 'All' ? items : items.filter(m => m.category === activeCategory));
  }, [activeCategory, items]);

  const openLightbox = useCallback((idx) => setLightboxIndex(idx), []);
  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const prevItem = useCallback(() => setLightboxIndex(i => (i - 1 + filtered.length) % filtered.length), [filtered.length]);
  const nextItem = useCallback(() => setLightboxIndex(i => (i + 1) % filtered.length), [filtered.length]);

  const title = config?.gallery?.title || 'Our Gallery';
  const subtitle = config?.gallery?.subtitle || 'A glimpse into the compassionate care we deliver every day.';

  if (!loading && items.length === 0) return null;

  return (
    <section style={{
      background: COLOR.bgMarketing, padding: '80px 24px',
    }}>
      <div style={{ maxWidth: '100%', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 48 }}>
          <span style={{
            display: 'inline-block', marginBottom: 12,
            fontSize: 12, fontWeight: 700, letterSpacing: '0.1em',
            textTransform: 'uppercase', color: COLOR.primary,
          }}>Media</span>
          <h2 style={{ fontSize: 36, fontWeight: 800, color: COLOR.text, margin: '0 0 12px' }}>
            {title}
          </h2>
          <p style={{ color: COLOR.textMuted, fontSize: 17, maxWidth: 520, margin: '0 auto' }}>
            {subtitle}
          </p>
        </div>

        {/* Category filter */}
        {categories.length > 1 && (
          <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 40 }}>
            {categories.map(cat => (
              <button key={cat} onClick={() => setActiveCategory(cat)} style={{
                padding: '7px 20px', borderRadius: 100,
                border: '2px solid',
                borderColor: activeCategory === cat ? COLOR.primary : COLOR.border,
                background: activeCategory === cat ? COLOR.primary : '#fff',
                color: activeCategory === cat ? '#fff' : COLOR.textMuted,
                fontWeight: 600, fontSize: 13, cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}>{cat}</button>
            ))}
          </div>
        )}

        {/* Loading skeleton */}
        {loading && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 20 }}>
            {[1,2,3,4,5,6].map(i => (
              <div key={i} style={{
                borderRadius: 12, background: COLOR.border,
                aspectRatio: '4/3', animation: 'pulse 1.5s ease-in-out infinite',
              }} />
            ))}
          </div>
        )}

        {/* Grid */}
        {!loading && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 20,
          }}>
            {filtered.map((item, idx) => (
              <MediaCard key={item.media_id} item={item} onClick={() => openLightbox(idx)} />
            ))}
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <p style={{ textAlign: 'center', color: COLOR.textFaint, fontSize: 15 }}>
            No media in this category yet.
          </p>
        )}
      </div>

      {lightboxIndex !== null && (
        <Lightbox
          item={filtered[lightboxIndex]}
          onClose={closeLightbox}
          onPrev={prevItem}
          onNext={nextItem}
        />
      )}
    </section>
  );
}
